import React, {useRef, Fragment} from "react"
import {toast} from "react-toastify"
import useAuth from "auth/useAuth"
import "./RescueAttachments.css"
import {shortenFilename, errorMessage, responseError} from "utils/utils"
import {compressImage} from "utils/image_utils"

export default function RescueAttachments(props) {

    const {rescue} = props
    const {auth, API, getToken, endpoint} = useAuth()
    const galleryInputRef = useRef(null)
    const cameraInputRef = useRef(null)
    const audioInputRef = useRef(null)


    const uploadAttachment = async (file) => {
        const isImage = file.type.startsWith('image/')
        const isAudio = file.type.startsWith('audio/')
        const toaster = toast.loading(isAudio ? "Slanje audio fajla..." : "Slanje slike...")

        try {
            // Compress image before uploading, but not audio files
            const fileToUpload = isImage ? await compressImage(file) : file

            const token = await getToken()
            const url = endpoint + "/attachments"
            const formData = new FormData()
            formData.append('file', fileToUpload)
            formData.append('filename', file.name)
            if (rescue.rescue?.id) formData.append('rescue_id', rescue.rescue.id)

            const response = await fetch(url, {
                method: "POST",
                headers: {'Authorization': token},
                body: formData
            })

            if (!response.ok) throw await responseError(response)
            const data = await response.json()

            // Add attachment ID to rescue
            const attachments = rescue.rescue.attachments || []
            rescue.setField('attachments', [...attachments, data])

            toast.update(toaster, {render: isAudio ? "Audio fajl je dodat" : "Slika je dodata", type: "success", isLoading: false, autoClose: 2500})
        } catch (error) {
            console.error("ERROR", error)
            const what = isAudio ? "Greška pri slanju audio fajla" : "Greška pri slanju slike"
            toast.update(toaster, {render: `${what}: ${errorMessage(error)}`, type: "error", isLoading: false, autoClose: 2500})
        }
    }

    const handleFileChange = (event) => {
        const file = event.target.files[0]
        if (file) {
            uploadAttachment(file)
        }
        // Reset input so the same file can be selected again
        event.target.value = null
    }

    const openGallery = () => {
        galleryInputRef.current?.click()
    }

    const openCamera = () => {
        cameraInputRef.current?.click()
    }

    const openAudio = () => {
        audioInputRef.current?.click()
    }

    const fetchAttachment = (attachment) => {
        return auth(API.attachmentsIdGet, {id: attachment.id}).then(response => {
            // Convert base64 to blob
            const byteString = atob(response.data.content)
            const ab = new ArrayBuffer(byteString.length)
            const ia = new Uint8Array(ab)
            for (let i = 0; i < byteString.length; i++) {
                ia[i] = byteString.charCodeAt(i)
            }
            const blob = new Blob([ia], {type: response.data['content-type']})
            return blob
        })
    }

    const openAttachment = (attachment) => {
        const toaster = toast.loading("Učitavanje...")

        fetchAttachment(attachment).then(blob => {
            toast.dismiss(toaster)

            // Create URL and open in new tab
            const url = URL.createObjectURL(blob)
            window.open(url, '_blank')

            // Clean up URL after a delay
            setTimeout(() => URL.revokeObjectURL(url), 1000)
        }).catch(error => {
            console.error("ERROR", error)
            toast.update(toaster, {render: "Greška pri učitavanju fajla", type: "error", isLoading: false, autoClose: 2500})
        })
    }

    const downloadAttachment = (attachment) => {
        const toaster = toast.loading("Preuzimanje...")

        fetchAttachment(attachment).then(blob => {
            toast.dismiss(toaster)

            // Create URL and trigger download
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = attachment.filename
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)

            // Clean up URL after a delay
            setTimeout(() => URL.revokeObjectURL(url), 1000)
        }).catch(error => {
            console.error("ERROR", error)
            toast.update(toaster, {render: "Greška pri preuzimanju fajla", type: "error", isLoading: false, autoClose: 2500})
        })
    }

    return <section id="rescue-attachments">

        {rescue.rescue?.attachments?.length > 0 && <div id="attachment-list">
            {rescue.rescue?.attachments?.map(attachment => {
                const isAudio = attachment.filename?.match(/\.(mp3|wav|ogg|m4a|aac|flac|wma)$/i)
                return <Fragment key={attachment.id}>
                    <div className="attachment-name">
                        <i className={isAudio ? "fa-solid fa-music" : "fa-solid fa-paperclip"}/>
                        <a href="#" title={attachment.filename} onClick={(e) => {e.preventDefault(); openAttachment(attachment)}}>
                            {shortenFilename(attachment.filename)}
                        </a>
                    </div>
                    <div className="attachment-actions">
                        <button className="btn btn-sm btn-light" onClick={() => downloadAttachment(attachment)} title="Preuzmi">
                            <i className="fas fa-download"/>
                        </button>
                    </div>
                </Fragment>
            })}
        </div>}

        {rescue.permissions?.save && <>
            <div id="attachment-buttons">
                <button className="btn btn-secondary" onClick={openGallery}>
                    <span>Prikači sliku</span><i className="fas fa-images"/>
                </button>
                <button className="btn btn-secondary" onClick={openCamera}>
                    <i className="fas fa-camera"/>
                </button>
                <button className="btn btn-secondary" onClick={openAudio}>
                    <i className="fas fa-microphone"/>
                </button>
            </div>

            {/* Hidden file inputs */}
            <input type="file" accept="image/*" ref={galleryInputRef} onChange={handleFileChange} style={{display: 'none'}}/>
            <input type="file" accept="image/*" capture="environment" ref={cameraInputRef} onChange={handleFileChange} style={{display: 'none'}}/>
            <input type="file" accept="audio/*" capture ref={audioInputRef} onChange={handleFileChange} style={{display: 'none'}}/>
        </>}
    </section>
}
